import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearInquiryDetail,
  createInquiryAnswer,
  fetchInquiryDetail,
} from "../../store/slices/inquiriesSlice";
import { formatDateTimeDot, formatInquiryType } from "../../utils/admin";

const ANSWER_MAX = 1000;

const InquiryDetailPage = () => {
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const numericId = Number(inquiryId);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { detail, loadingDetail, detailError, savingAnswer } = useAppSelector((state) => state.inquiries);
  const [answerText, setAnswerText] = useState("");

  useEffect(() => {
    if (!Number.isFinite(numericId) || numericId <= 0) {
      return;
    }
    dispatch(fetchInquiryDetail(numericId));
    return () => {
      dispatch(clearInquiryDetail());
    };
  }, [dispatch, numericId]);

  useEffect(() => {
    setAnswerText(detail?.answer ?? "");
  }, [detail]);

  const onSubmitAnswer = async () => {
    const trimmed = answerText.trim();
    if (!trimmed) {
      window.alert("답변을 입력해 주세요.");
      return;
    }
    if (trimmed.length > ANSWER_MAX) {
      window.alert(`답변은 최대 ${ANSWER_MAX}자까지 입력할 수 있습니다.`);
      return;
    }

    try {
      await dispatch(createInquiryAnswer({ inquiryId: numericId, answer: trimmed })).unwrap();
      window.alert("답변이 발송되었습니다.");
      dispatch(fetchInquiryDetail(numericId));
    } catch (error) {
      window.alert((error as string) || "답변 저장에 실패했습니다.");
    }
  };

  if (!Number.isFinite(numericId) || numericId <= 0) {
    return (
      <section className="admin-page">
        <p className="error-text">유효하지 않은 문의입니다.</p>
      </section>
    );
  }

  const attachments = detail?.attachment_urls?.filter(Boolean) ?? [];
  const isAnswered = Boolean(detail?.is_answered);

  return (
    <section className="admin-page inquiry-detail-page">
      <div className="inquiry-detail-page__inner">
        <div className="inquiry-detail-page__header">
          <button
            className="inquiry-detail-page__back"
            type="button"
            onClick={() => navigate("/admin/inquiries")}
            aria-label="목록으로"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
              <path
                d="M12.5 4.5L7 10l5.5 5.5"
                stroke="#212529"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h2 className="inquiry-detail-page__title">문의 상세</h2>
        </div>

        {loadingDetail && <p className="loading-text">문의 상세를 불러오는 중입니다...</p>}
        {detailError && <p className="error-text">{detailError}</p>}

        {detail && (
          <>
            <div className="inquiry-detail-meta">
              <span
                className={`inquiry-badge ${
                  isAnswered ? "inquiry-badge--done" : "inquiry-badge--pending"
                }`}
              >
                {isAnswered ? "답변완료" : "답변대기"}
              </span>
              <p className="inquiry-detail-meta__title">
                <span>[{formatInquiryType(detail.inquiry_type)}]</span>
                {` ${detail.title}`}
              </p>
              <p className="inquiry-detail-meta__sub">
                작성자: {detail.user_name}｜ 등록일시: {formatDateTimeDot(detail.created_at)}
              </p>
            </div>

            <div className="inquiry-detail-block">
              <h4>문의 내용</h4>
              <div className="inquiry-detail-content">{detail.content}</div>
            </div>

            {attachments.length > 0 && (
              <div className="inquiry-detail-block inquiry-detail-block--attachments">
                <h4>첨부사진</h4>
                <div className="inquiry-detail-attachments">
                  {attachments.map((url) => (
                    <a key={url} href={url} target="_blank" rel="noreferrer">
                      <img src={url} alt="문의 첨부" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="inquiry-answer-block">
              <h4>{isAnswered ? "관리자 답변" : "관리자 답변 등록"}</h4>

              {isAnswered ? (
                <div className="inquiry-detail-content inquiry-detail-content--answer">
                  {detail.answer || "-"}
                </div>
              ) : (
                <>
                  <textarea
                    className="inquiry-answer-textarea"
                    placeholder="고객에게 전달할 답변을 입력하세요."
                    value={answerText}
                    maxLength={ANSWER_MAX}
                    onChange={(event) => setAnswerText(event.target.value)}
                  />
                  <div className="inquiry-answer-footer">
                    <button
                      className="inquiry-answer-submit"
                      type="button"
                      disabled={savingAnswer || !answerText.trim()}
                      onClick={onSubmitAnswer}
                    >
                      {savingAnswer ? "저장 중..." : "답변 발송"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default InquiryDetailPage;
